import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { toast } from 'sonner';
import { 
  Plus, 
  Search,
  Target,
  CheckCircle2,
  Clock,
  Lightbulb,
  ChevronRight,
  Calendar,
  Flag
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { PageHeader, TabGroup, HTCard, HTEmptyState, StatusBadge } from '../../../components/HumaTwinUI';

export default function PersonalGoals() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewGoalDialog, setShowNewGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: 'personal',
    timeline: 'ongoing'
  });
  const [saving, setSaving] = useState(false);

  const tabs = [
    { id: 'active', label: t('goals.tabs.active') || 'Active' },
    { id: 'completed', label: t('goals.tabs.completed') || 'Completed' },
    { id: 'ideas', label: t('goals.tabs.ideas') || 'Ideas' },
  ];

  const categories = [
    { value: 'personal', label: t('goals.categories.personal') || 'Personal', color: 'blue' },
    { value: 'business', label: t('goals.categories.business') || 'Business', color: 'green' },
    { value: 'family', label: t('goals.categories.family') || 'Family', color: 'amber' },
    { value: 'health', label: t('goals.categories.health') || 'Health', color: 'purple' },
  ];

  const timelines = [
    { value: 'ongoing', label: t('goals.timelines.ongoing') || 'Ongoing' },
    { value: 'short', label: t('goals.timelines.short') || 'Short-term (1-3 months)' },
    { value: 'medium', label: t('goals.timelines.medium') || 'Medium-term (3-12 months)' },
    { value: 'long', label: t('goals.timelines.long') || 'Long-term (1+ years)' },
  ];

  useEffect(() => {
    fetchGoals();
  }, [activeTab]);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/personal/goals?status=${activeTab}`);
      setGoals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.title.trim()) {
      toast.error(t('goals.validation.titleRequired') || 'Goal title is required');
      return;
    }

    try {
      setSaving(true);
      await apiClient.post('/personal/goals', newGoal);
      toast.success(t('goals.success.created') || 'Goal created successfully');
      setShowNewGoalDialog(false);
      setNewGoal({ title: '', description: '', category: 'personal', timeline: 'ongoing' });
      fetchGoals();
    } catch (error) {
      toast.error(t('goals.error.create') || 'Failed to create goal');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryBadge = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'gray';
  };

  const filteredGoals = goals.filter(g => 
    !searchQuery || g.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="goals-page">
        <Skeleton className="h-10 w-48 mb-2" />
        <Skeleton className="h-5 w-72 mb-8" />
        <Skeleton className="h-12 w-full mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="goals-page">
      {/* Header */}
      <PageHeader
        title={t('goals.title') || 'Goals'}
        subtitle={t('goals.subtitle') || 'Turn your vision into action'}
        actionLabel={t('goals.newGoal') || '+ New Goal'}
        actionIcon={Plus}
        onAction={() => setShowNewGoalDialog(true)}
      />

      {/* Tabs */}
      <div className="mb-6">
        <TabGroup 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder={t('common.search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-xl"
          data-testid="goals-search"
        />
      </div>

      {/* Goals List */}
      {filteredGoals.length === 0 ? (
        <HTEmptyState
          icon={Target}
          title={t('goals.empty.title') || 'No goals yet'}
          description={t('goals.empty.description') || 'Set your first goal to start tracking your progress.'}
          actionLabel={t('goals.empty.action') || 'Create Goal'}
          onAction={() => setShowNewGoalDialog(true)}
        />
      ) : (
        <div className="space-y-3">
          {filteredGoals.map((goal, index) => (
            <HTCard
              key={goal.id || index}
              className="p-4 cursor-pointer hover:shadow-card-hover transition-all group"
              data-testid={`goal-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  goal.status === 'completed' 
                    ? 'bg-green-50 group-hover:bg-green-100' 
                    : 'bg-amber-50 group-hover:bg-amber-100'
                }`}>
                  {goal.status === 'completed' 
                    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : <Target className="w-5 h-5 text-amber-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 truncate">
                      {goal.title}
                    </h3>
                  </div>
                  {goal.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                      {goal.description}
                    </p>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    <StatusBadge variant={getCategoryBadge(goal.category)}>
                      {categories.find(c => c.value === goal.category)?.label || goal.category}
                    </StatusBadge>
                    <StatusBadge variant="gray">
                      <Clock className="w-3 h-3 mr-1" />
                      {timelines.find(t => t.value === goal.timeline)?.label || goal.timeline}
                    </StatusBadge>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
              </div>
            </HTCard>
          ))}
        </div>
      )}

      {/* New Goal Dialog */}
      <Dialog open={showNewGoalDialog} onOpenChange={setShowNewGoalDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('goals.dialog.title') || 'Create New Goal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="goal-title">{t('goals.dialog.goalTitle') || 'Goal Title'}</Label>
              <Input
                id="goal-title"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                placeholder={t('goals.dialog.titlePlaceholder') || 'What do you want to achieve?'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal-description">{t('goals.dialog.description') || 'Description'}</Label>
              <Textarea
                id="goal-description"
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder={t('goals.dialog.descriptionPlaceholder') || 'Describe your goal in more detail...'}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('goals.dialog.category') || 'Category'}</Label>
                <Select value={newGoal.category} onValueChange={(v) => setNewGoal({ ...newGoal, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('goals.dialog.timeline') || 'Timeline'}</Label>
                <Select value={newGoal.timeline} onValueChange={(v) => setNewGoal({ ...newGoal, timeline: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timelines.map(tl => (
                      <SelectItem key={tl.value} value={tl.value}>{tl.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGoalDialog(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleCreateGoal} disabled={saving}>
              {saving ? t('common.saving') || 'Saving...' : t('goals.dialog.create') || 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
